import { endOfDay, format, startOfDay } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useActiveUserWorkItems } from "../../hooks/useActiveUserWorkItems";
import { RootState } from "../../store";
import { useGetCalendarEventsQuery } from "../../store/ms-services/calendarApi";
import {
  CalendarItem,
  eventOccursOnDay,
  mapCalendarEvents,
  mapWorkItems,
  mergeRelatedItems,
} from "../calendar/calendarModel";
import { BusyInterval, computeFreeSlots } from "./freeSlotUtils";

export interface UseFreeSlotsOptions {
  excludeCalendarEventId?: string;
  excludeWorkItemId?: number;
}

export interface FreeSlotsResult {
  slots: Date[];
  busyCount: number;
  hasAllDayEvent: boolean;
  allDayTitles: string[];
  isLoading: boolean;
  isError: boolean;
}

const isExcluded = (
  item: CalendarItem,
  options?: UseFreeSlotsOptions
): boolean => {
  const { excludeCalendarEventId, excludeWorkItemId } = options ?? {};
  if (excludeCalendarEventId) {
    if (
      item.id === excludeCalendarEventId ||
      item.relatedItems?.calendarEvent?.id === excludeCalendarEventId
    ) {
      return true;
    }
  }
  if (excludeWorkItemId !== undefined) {
    if (
      item.id === `workitem-${excludeWorkItemId}` ||
      item.relatedItems?.workItem?.Id === excludeWorkItemId
    ) {
      return true;
    }
  }
  return false;
};

export const useFreeSlots = (
  date: Date | null,
  durationMinutes: number,
  options?: UseFreeSlotsOptions
): FreeSlotsResult => {
  const hasMsAuth = useSelector((state: RootState) =>
    Boolean(state.auth.azure_token)
  );

  // Dagsgranulär nyckel så att tangenttryck i en DateTimePicker (minuter,
  // timmar) aldrig ändrar RTK-cacheargumentet
  const dayKey =
    date && !isNaN(date.getTime())
      ? format(startOfDay(date), "yyyy-MM-dd")
      : null;

  // Debounce av dagbyten (skriva årtal siffra för siffra ska inte ge en
  // hämtning per mellanliggande dag); första värdet sätts direkt
  const [debouncedDayKey, setDebouncedDayKey] = useState<string | null>(dayKey);
  const hasValueRef = useRef(dayKey !== null);
  useEffect(() => {
    if (!hasValueRef.current && dayKey !== null) {
      hasValueRef.current = true;
      setDebouncedDayKey(dayKey);
      return;
    }
    const handle = setTimeout(() => setDebouncedDayKey(dayKey), 300);
    return () => clearTimeout(handle);
  }, [dayKey]);

  const day = debouncedDayKey ? new Date(`${debouncedDayKey}T00:00:00`) : null;
  const dayIsPast = day !== null && day < startOfDay(new Date());
  const skip = !day || !hasMsAuth || dayIsPast;

  const dayStartIso = day ? startOfDay(day).toISOString() : "";
  const dayEndIso = day ? endOfDay(day).toISOString() : "";

  const {
    data: calendarEvents = [],
    isFetching: isCalendarFetching,
    isError: isCalendarError,
  } = useGetCalendarEventsQuery(
    { startDateTime: dayStartIso, endDateTime: dayEndIso },
    { skip }
  );

  const { data: workItems = [], isFetching: isWorkItemsFetching } =
    useActiveUserWorkItems({ fromDate: dayStartIso, toDate: dayEndIso }, { skip });

  return useMemo(() => {
    if (skip || !day) {
      return {
        slots: [],
        busyCount: 0,
        hasAllDayEvent: false,
        allDayTitles: [],
        isLoading: false,
        isError: !hasMsAuth,
      };
    }

    if (isCalendarError) {
      return {
        slots: [],
        busyCount: 0,
        hasAllDayEvent: false,
        allDayTitles: [],
        isLoading: false,
        isError: true,
      };
    }

    const merged = mergeRelatedItems(
      mapCalendarEvents(calendarEvents),
      mapWorkItems(workItems)
    ).filter((item) => !isExcluded(item, options));

    const allDayItems = merged.filter(
      (item) => item.isAllDay && eventOccursOnDay(item, day)
    );
    const timedItems = merged.filter(
      (item) => !item.isAllDay && eventOccursOnDay(item, day)
    );

    const busy: BusyInterval[] = timedItems.map((item) => ({
      start: item.start,
      end: item.end,
    }));

    // Returnera samtliga lediga tider — visningsurvalet görs i komponenten
    const freeSlots = computeFreeSlots(day, durationMinutes, busy);

    return {
      slots: freeSlots,
      busyCount: timedItems.length,
      hasAllDayEvent: allDayItems.length > 0,
      allDayTitles: allDayItems.map((item) => item.title),
      isLoading: isCalendarFetching || isWorkItemsFetching,
      isError: false,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    skip,
    debouncedDayKey,
    hasMsAuth,
    isCalendarError,
    calendarEvents,
    workItems,
    durationMinutes,
    isCalendarFetching,
    isWorkItemsFetching,
    options?.excludeCalendarEventId,
    options?.excludeWorkItemId,
  ]);
};
